import { Box, Collapse } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import ModificationForm from '@acorn-portal/shared/src/components/forms/ModificationForm';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { formatISO, isAfter, subYears } from 'date-fns';
import { Form, Formik, FormikHelpers } from 'formik';
import { useLocation, useNavigate } from 'react-router';
import { Address, Modification } from '@acorn-portal/shared/src/services/api';
import { VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useEffect, useState } from 'react';
import {
	COVER_LEVEL,
	COVER_LEVEL_UPDATED,
	TAXI_VEHICLE_USE,
	VEHICLE_LEFT_HAND_DRIVE,
	OVERNIGHT_ADDRESS_DIFFERENT,
} from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { updateChangeOfVehicle } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import useRateRenewal from '@acorn-portal/shared/src/hooks/useRateRenewal';
import CvAdditionalQuestions from '@acorn-portal/shared/src/components/forms/CvAdditionalQuestions';
import { Product, TelematicDeviceProvider } from '@acorn-portal/shared/src/services/interface/Shared';
import TelematicQuestions from '@acorn-portal/shared/src/components/forms/TelematicQuestions';
import useRenewalNavigation from '@acorn-portal/shared/src/hooks/useRenewalNavigation';
import { isNotInFutureMonth } from '@acorn-portal/shared/src/utils';
import { ESTIMATED_VALUE_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import ChangeOfVehiclePart2Schema from '@acorn-portal/shared/src/utils/validation/ChangeOfVehiclePart2Schema';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import { useVehicleUsageOptions } from '@acorn-portal/shared/src/utils/taxiVehicleUsageOptions';
import hasOverNightAddress from '../../utils/has-overnight-address';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IChangeOfVehiclePart2 {
	registeredKeeper: string;
	owner: string;
	hasModifications: string;
	imported: string;
	value: string;
	purchaseDate: {
		year: string;
		month: string;
	};
	levelOfCover: string;
	countryOfRegistration: string;
	modifications: Modification[];
	isLeftHandDrive: string;
	businessRadius: string;
	hasInternalRacking: string;
	vehicleCarriesDangerousGoods: string;
	vehicleVisitsHazardousSites: string;
	telematicsBox: string;
	bodyType?: string;
	vehicleUse?: string;
	keptOvernight: string;
	overnightAddress: Address;
	overnightAddressIsAway: string;
}

type LocationProps = {
	state: {
		registration: string;
		yearOfManufacture: number;
		bodyType: string;
	};
};

const ChangeOfVehiclePart2 = () => {
	const [open, toggleModal] = useState(false);
	const { goToRenewalHome } = useRenewalNavigation();
	const renewalState = useAppSelector((redux) => redux.renewal);
	const vehicleLookup = useAppSelector((redux) => redux.vehicleLookup);
	const lists = useAppSelector((redux) => redux.referenceData);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const { rateRenewal, loadingRenewalQuote } = useRateRenewal();
	const location = useLocation() as unknown as LocationProps;
	const isMyPolicy = fullPolicy.product === 'TELEMATICS' && import.meta.env.VITE_AUTH_REALM === 'MyPolicy';

	const vehicleUsageOptions = useVehicleUsageOptions();

	useEffect(() => {
		setLoading(loadingRenewalQuote);
		if (!renewalState.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, [loadingRenewalQuote, location.state, renewalState.policyReference]);

	const initialValues: IChangeOfVehiclePart2 = {
		registeredKeeper: '',
		owner: '',
		hasModifications: '',
		imported: '',
		value: '',
		levelOfCover: isMyPolicy ? 'COMP' : '',
		purchaseDate: { year: '', month: '' },
		countryOfRegistration: '',
		modifications: [],
		isLeftHandDrive: '',
		businessRadius: '',
		telematicsBox: '',
		hasInternalRacking: '',
		vehicleCarriesDangerousGoods: '',
		vehicleVisitsHazardousSites: '',
		vehicleUse: '',
		keptOvernight: renewalState.vehicleDetails.vehicleOvernight || '',
		overnightAddressIsAway: hasOverNightAddress(
			renewalState.policyDetails.policyholder.address,
			renewalState.vehicleDetails.overnightAddress ?? ({} as Address),
		)
			? 'yes'
			: 'no',
		overnightAddress: {
			...(renewalState.vehicleDetails.overnightAddress || {
				line1: '',
				city: '',
				postcode: '',
			}),
		},
	};

	const handleOnSubmit = async (values: IChangeOfVehiclePart2, formikHelpers: FormikHelpers<IChangeOfVehiclePart2>) => {
		const datePurchased = new Date(
			parseInt(values.purchaseDate.year, 10),
			parseInt(values.purchaseDate.month, 10) - 1,
			1,
		);
		const manufactureDate = new Date(vehicleLookup.vehicleInfo.year as number, 0, 1);
		if (isAfter(manufactureDate, datePurchased)) {
			formikHelpers.setErrors({
				purchaseDate: {
					year: `${VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES.purchaseMinDate} (${manufactureDate.getFullYear()})`,
				},
			});
			return;
		}

		if (!isNotInFutureMonth(datePurchased)) {
			formikHelpers.setErrors({
				purchaseDate: {
					month: `${VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES.purchaseMaxDate}`,
				},
			});
			return;
		}

		const { vehicleInfo } = vehicleLookup;

		if (fullPolicy.product !== Product.TAXI) {
			values.vehicleUse = renewalState.vehicleDetails.vehicleUse ?? '';
		}

		const renewalUpdated = {
			...renewalState,
			vehicleDetails: {
				...renewalState.vehicleDetails,
				...vehicleInfo,
				bodyType: location.state.bodyType ?? vehicleInfo.bodyType?.code ?? '',
				numberOfDoors: vehicleInfo.numberOfDoors?.code ?? '',
				registeredKeeper: values.registeredKeeper,
				abiCode: vehicleInfo.abiCode ?? '',
				engineSize: vehicleInfo.cubicCapacity ?? 0,
				abiGroup: parseInt(vehicleInfo.abiGroup ?? '0', 10),
				fuelType: vehicleInfo.fuelType?.code ?? '',
				transmission: vehicleInfo.transmission?.code ?? '',
				ownership: values.owner,
				isImported: values.imported === 'yes',
				value: parseInt(values.value, 10),
				make: vehicleInfo.make?.code ?? '',
				model: vehicleInfo.model?.code ?? '',
				coverDefinitionCode: values.levelOfCover,
				modifications: values.modifications,
				datePurchased: formatISO(datePurchased),
				countryOfRegistration: values.countryOfRegistration,
				telematicsBox: values.telematicsBox === 'yes',
				registration: location.state.registration,
				yearOfManufacture: location.state.yearOfManufacture,
				businessRadius: values.businessRadius,
				hasInternalRacking: values.hasInternalRacking === 'yes',
				vehicleCarriesDangerousGoods: values.vehicleCarriesDangerousGoods === 'yes',
				vehicleVisitsHazardousSites: values.vehicleVisitsHazardousSites === 'yes',
				vehicleUse: values.vehicleUse ?? '',
				vehicleOvernight: values.keptOvernight,
				// Handle overnight address based on user's final choice
				overnightAddress:
					values.overnightAddressIsAway === 'yes'
						? values.overnightAddress
						: renewalState.policyDetails.policyholder.address,
				isLeftHandDrive: values.isLeftHandDrive === 'yes',
				grossWeight: parseInt(vehicleInfo.grossWeight ?? '0', 10),
			},
			overnightAddressIsAway: values.overnightAddressIsAway || '',
		};

		try {
			setLoading(true);
			dispatch(updateChangeOfVehicle(true));
			await rateRenewal({ renewalState: renewalUpdated, fullPolicy });
		} catch (error) {
			console.error(error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};
	const isTaxi = fullPolicy.product === Product.TAXI;

	const deviceProvider = fullPolicy.coverDetails.deviceProvider;
	const hasSelfFitDevice =
		deviceProvider !== undefined &&
		[TelematicDeviceProvider.THINGCO, TelematicDeviceProvider.TRAKM8].includes(
			deviceProvider as TelematicDeviceProvider,
		);

	const renewalCoverStartDate = fullPolicy.renewalDetails?.renewalCoverDetails.startDate;

	return (
		<JourneyCardLayout pageTitle='New Vehicle Details' loading={loading}>
			<Formik
				key={JSON.stringify(renewalState.newRenewalTransactionId)}
				initialValues={initialValues}
				onSubmit={handleOnSubmit}
				validationSchema={ChangeOfVehiclePart2Schema({
					state: renewalState,
					fullPolicy,
				})}
				onReset={goToRenewalHome}
			>
				{(formik) => {
					return (
						<Form>
							<FormikLogErrors />
							{isTaxi && (
								<ToggleButtonFormField
									label='What will you be using the vehicle for?'
									name='vehicleUse'
									id='vehicleUse'
									value={formik.values.vehicleUse}
									onChange={(value) => {
										formik.setFieldValue('vehicleUse', value);
									}}
									options={vehicleUsageOptions}
									error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
									helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
									modal={{
										title: TAXI_VEHICLE_USE.header,
										description: TAXI_VEHICLE_USE.text,
									}}
									product={fullPolicy.product}
								/>
							)}
							<AutoCompleteFormField
								label={
									isTaxi
										? 'Who will be the registered owner and keeper from the start date of your policy?'
										: 'Who currently owns the vehicle?'
								}
								name='owner'
								id='owner'
								value={formik.values.owner}
								onChange={(value) => formik.setFieldValue('owner', value)}
								options={lists.vehicleOwnershipOptions}
								error={formik.touched.owner && Boolean(formik.errors.owner)}
								helperText={formik.touched.owner && formik.errors.owner}
							/>
							<AutoCompleteFormField
								label='Who is the registered keeper of the vehicle?'
								name='registeredKeeper'
								id='registeredKeeper'
								value={formik.values.registeredKeeper}
								onChange={(value) => formik.setFieldValue('registeredKeeper', value)}
								options={lists.vehicleKeeperOptions}
								error={formik.touched.registeredKeeper && Boolean(formik.errors.registeredKeeper)}
								helperText={formik.touched.registeredKeeper && formik.errors.registeredKeeper}
							/>
							<RadioGroupButton
								label='Has the vehicle been modified?'
								name='hasModifications'
								id='hasModifications'
								value={formik.values.hasModifications}
								onChange={(value) => formik.setFieldValue('hasModifications', value)}
								error={
									(formik.touched.hasModifications && Boolean(formik.errors.hasModifications)) ||
									(formik.touched.hasModifications && Boolean(formik.errors.modifications))
								}
								helperText={
									(formik.touched.hasModifications && formik.errors.hasModifications) ||
									(formik.touched.hasModifications && (formik.errors.modifications as string))
								}
							/>
							<Collapse in={formik.values.hasModifications === 'yes'}>
								<ModificationForm
									hasGlobalError={formik.touched.hasModifications && Boolean(formik.errors.modifications)}
									onChange={(value) => formik.setFieldValue('modifications', value)}
									value={formik.values.modifications}
								/>
							</Collapse>
							<RadioGroupButton
								label='Is the vehicle imported?'
								name='imported'
								id='imported'
								value={formik.values.imported}
								onChange={(value) => {
									formik.setFieldValue('imported', value);
									if (value === 'no') {
										formik.setFieldValue('countryOfRegistration', '', false);
									}
								}}
								error={formik.touched.imported && Boolean(formik.errors.imported)}
								helperText={formik.touched.imported && formik.errors.imported}
							/>
							<Collapse in={formik.values.imported === 'yes'}>
								<Box>
									<AutoCompleteFormField
										label='Initial Country of Registration'
										name='countryOfRegistration'
										id='countryOfRegistration'
										value={formik.values.countryOfRegistration}
										onChange={(value) => formik.setFieldValue('countryOfRegistration', value)}
										options={lists.countryEarnedNcdOptions}
										error={formik.touched.countryOfRegistration && Boolean(formik.errors.countryOfRegistration)}
										helperText={formik.touched.countryOfRegistration && formik.errors.countryOfRegistration}
									/>
								</Box>
							</Collapse>
							<RadioGroupButton
								label='Is the vehicle left-hand drive?'
								name='isLeftHandDrive'
								id='isLeftHandDrive'
								value={formik.values.isLeftHandDrive}
								onChange={(value) => formik.setFieldValue('isLeftHandDrive', value)}
								error={formik.touched.isLeftHandDrive && Boolean(formik.errors.isLeftHandDrive)}
								helperText={formik.touched.isLeftHandDrive && formik.errors.isLeftHandDrive}
								modal={{
									title: VEHICLE_LEFT_HAND_DRIVE.header,
									description: VEHICLE_LEFT_HAND_DRIVE.text,
								}}
							/>
							<DatePickerField
								id='purchaseDate'
								onChange={(value) => formik.setFieldValue('purchaseDate', value)}
								label='Purchased Date'
								value={formik.values.purchaseDate}
								error={
									(formik.touched.purchaseDate?.month && Boolean(formik.errors.purchaseDate?.month)) ||
									(formik.touched.purchaseDate?.year && Boolean(formik.errors.purchaseDate?.year))
								}
								helperText={
									(formik.touched.purchaseDate?.month && formik.errors.purchaseDate?.month) ||
									(formik.touched.purchaseDate?.year && formik.errors.purchaseDate?.year)
								}
								day={false}
								minYear={subYears(new Date(), 25).getFullYear()}
							/>
							<ToggleButtonFormField
								label='Where is the vehicle kept overnight?'
								name='keptOvernight'
								id='keptOvernight'
								value={formik.values.keptOvernight}
								onChange={(value) => formik.setFieldValue('keptOvernight', value)}
								options={lists.vehicleStorageOptions as IOptions[]}
								error={formik.touched.keptOvernight && Boolean(formik.errors.keptOvernight)}
								helperText={formik.touched.keptOvernight && formik.errors.keptOvernight}
							/>
							<RadioGroupButton
								label="Is the overnight address different from the policyholder's address?"
								name='overnightAddressIsAway'
								id='overnightAddressIsAway'
								value={formik.values.overnightAddressIsAway}
								onChange={(value) => {
									formik.setFieldValue('overnightAddressIsAway', value);
									formik.setFormikState((x) => ({
										...x,
										values: {
											...x.values,
											overnightAddress: {
												line1: '',
												line2: undefined,
												line3: undefined,
												line4: undefined,
												city: '',
												postcode: '',
											},
										},
									}));
								}}
								error={formik.touched.overnightAddressIsAway && Boolean(formik.errors.overnightAddressIsAway)}
								helperText={formik.touched.overnightAddressIsAway && formik.errors.overnightAddressIsAway}
								modal={{
									title: OVERNIGHT_ADDRESS_DIFFERENT.title,
									description: OVERNIGHT_ADDRESS_DIFFERENT.description,
								}}
							/>
							<Collapse in={formik.values.overnightAddressIsAway === 'yes'}>
								<AddressLookup
									initialValue={formik.values.overnightAddress}
									firstFieldLabel='Overnight House number or Name'
									postcodeLabel='Overnight Postcode'
									onChange={(value) => {
										formik.setFormikState((formState) => ({
											...formState,
											values: {
												...formState.values,
												overnightAddress: {
													...formState.values.overnightAddress,
													line1: value?.line1 ?? '',
													line2: value?.line2,
													line3: value?.line3,
													line4: value?.line4,
													city: value?.city ?? '',
													county: value?.county ?? '',
													country: value?.country ?? '',
													postcode: value?.postcode ?? '',
												},
											},
										}));
									}}
								/>
							</Collapse>
							<FormTextField
								id='value'
								name='value'
								label='Estimated Value (£)'
								error={formik.touched.value && Boolean(formik.errors.value)}
								helperText={formik.touched.value && formik.errors.value}
								onChange={formik.handleChange}
								value={formik.values.value}
								format='currency'
								onBlur={formik.handleBlur}
								modal={{
									title: ESTIMATED_VALUE_MODAL.header,
									description: ESTIMATED_VALUE_MODAL.text,
								}}
							/>
							{!isMyPolicy && (
								<SelectFormField
									label='Cover required'
									name='levelOfCover'
									id='levelOfCover'
									value={formik.values.levelOfCover}
									onChange={(value) => formik.setFieldValue('levelOfCover', value)}
									options={
										isTaxi
											? (lists.levelOfCoverTypes as IOptions[])
											: (lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[])
									}
									error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
									helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
									modal={
										renewalCoverStartDate
											? {
													title: (isTaxi ? COVER_LEVEL_UPDATED : COVER_LEVEL)(renewalCoverStartDate, fullPolicy.product)
														.header,
													description: (isTaxi ? COVER_LEVEL_UPDATED : COVER_LEVEL)(
														renewalCoverStartDate,
														fullPolicy.product,
													).text,
												}
											: undefined
									}
								/>
							)}

							{hasSelfFitDevice && <TelematicQuestions formik={formik} />}
							{fullPolicy.product === Product.COMMERCIAL_VEHICLE && <CvAdditionalQuestions formik={formik} />}
							<ActionButtons>
								<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
								<ContinueButton type='submit'>Continue</ContinueButton>
							</ActionButtons>
							<HelperModal
								open={open}
								onClose={() => {
									toggleModal(false);
								}}
								title='Discard The Changes?'
								description='Are you sure you wish to discard the changes?'
								closeText='No'
								submitText='Yes'
								onSubmit={() => {
									formik.resetForm();
								}}
							/>
						</Form>
					);
				}}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfVehiclePart2;
